import { Controller } from "@hotwired/stimulus";

// The final grade of a make-up, computed as it is typed (DU VAAL, 07/10/2026),
// by the rule the server applies at validation (Makeups::Blend):
//   final = original × original weight + make-up × make-up weight (100 %).
// Absent at the make-up: the original stays. No original grade (or absent):
// the make-up alone. In primary each competence is weighted, then the grade
// is the sum of the scores over the sum of the scales, brought to the
// evaluation's scale — as StudentGrade does.
export default class extends Controller {
  static targets = ["weight", "makeupWeight", "row"];
  static values = { scale: Number, separator: { type: String, default: "." }, aboveMessage: String };

  connect() {
    this.refresh();
  }

  refresh() {
    const weight = this.originalWeight();
    if (this.hasMakeupWeightTarget) this.makeupWeightTarget.textContent = String(100 - weight);
    this.rowTargets.forEach((row) => this.refreshRow(row, weight));
  }

  originalWeight() {
    if (!this.hasWeightTarget) return 50;
    const value = Math.round(Number(this.weightTarget.value));
    return Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 50;
  }

  refreshRow(row, weight) {
    const cell = row.querySelector("[data-final]");
    if (!cell || this.weightTarget?.disabled) return;
    const absent = row.querySelector("[data-absent]")?.checked;
    const original = this.number(row.dataset.original);
    const competenceInputs = Array.from(row.querySelectorAll("[data-competence]"));
    let final = null;
    // A grade above its scale is refused by the server: the field says so,
    // and no final grade is shown out of it.
    if (!absent && this.aboveScale(row, competenceInputs)) {
      cell.textContent = "—";
      return;
    }
    if (!absent) {
      final = competenceInputs.length ? this.byCompetence(row, competenceInputs, weight) : this.byScore(row, original, weight);
    }
    if (final === null) final = original;
    cell.textContent = final === null ? "—" : this.format(final);
  }

  aboveScale(row, competenceInputs) {
    const fields = competenceInputs.length
      ? competenceInputs.map((input) => [input, Number(input.dataset.scale)])
      : [[row.querySelector("[data-score]"), this.scaleValue]];
    let above = false;
    fields.forEach(([input, max]) => {
      if (!input) return;
      const value = this.number(input.value);
      const over = value !== null && max > 0 && value > max;
      input.setCustomValidity?.(over ? (this.aboveMessageValue || "").replace("%{max}", this.format(max)) : "");
      input.classList.toggle("ds-input--error", over);
      if (over) above = true;
    });
    return above;
  }

  byScore(row, original, weight) {
    const makeup = this.number(row.querySelector("[data-score]")?.value);
    if (makeup === null) return null;
    if (original === null) return makeup;
    return this.weigh(original, makeup, weight);
  }

  byCompetence(row, inputs, weight) {
    let originals = [];
    try { originals = JSON.parse(row.dataset.originalCompetences || "[]"); } catch (_e) { originals = []; }
    const before = new Map(originals.map((entry) => [String(entry.category_id), this.number(entry.score)]));
    const entries = inputs
      .map((input) => ({ score: this.number(input.value), scale: Number(input.dataset.scale), id: input.dataset.competence }))
      .filter((entry) => entry.score !== null);
    if (!entries.length) return null;
    const blended = entries.map((entry) => {
      const old = before.get(String(entry.id));
      return { score: old === null || old === undefined ? entry.score : this.weigh(old, entry.score, weight), scale: entry.scale };
    });
    const scales = blended.reduce((sum, entry) => sum + entry.scale, 0);
    const scores = blended.reduce((sum, entry) => sum + entry.score, 0);
    if (blended.every((entry) => entry.scale > 0) && scales > 0) {
      const target = this.scaleValue > 0 ? this.scaleValue : scales;
      return Math.round((scores / scales) * target * 100) / 100;
    }
    return Math.round((scores / blended.length) * 100) / 100;
  }

  weigh(original, makeup, weight) {
    return Math.round(((original * weight) + (makeup * (100 - weight))) / 100 * 100) / 100;
  }

  number(value) {
    if (value === undefined || value === null) return null;
    const text = String(value).trim().replace(",", ".");
    if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
    return Number(text);
  }

  // "10,8" in French: the decimal separator of the page's locale.
  format(value) {
    return String(Math.round(value * 100) / 100).replace(".", this.separatorValue || ".");
  }
}
